import { useState, useCallback, useEffect } from 'react';
import { saveWizardIgState, loadWizardIgState, clearWizardIgState } from './wizardIgPersistence';

const TOTAL_STEPS = 7;

const DEFAULT_BUSINESS = {
  name: '', description: '', address: '', phone: '', email: '', social: '',
  isPublic: true, infoFileName: '', infoFileSize: 0,
  type: '', typeOther: '',
  instagramEnabled: true, instagramHandle: '',
  facebookEnabled: false, facebookHandle: '',
};

const DEFAULT_PAYMENTS = {
  bank: { enabled: true, account: '' },
  sinpe: { enabled: true, number: '' },
  link: { enabled: false, url: '' },
  cash: { enabled: false },
};

const DEFAULT_ORDERS = {
  mode: 'orders', catalogFileName: '',
  fewProducts: false, simpleCatalogText: '',
  reservationDetails: '',
  escalation: '', escalationOther: '',
};

const DEFAULT_POLICIES = { text: '' };

const DEFAULT_INTERVIEW = { tone: '', salesFlow: '', messages: [], complete: false };

const DEFAULT_BUILD = {
  status: 'idle', // idle | running | done | error
  stages: { agent: 'pending', avatar: 'pending', workflow: 'pending' },
};

export function useWizardIgWizard() {
  const loaded = loadWizardIgState();
  // Un build ya terminado es un wizard "gastado" -- si lo retomáramos tal
  // cual, cada visita a /wizard-ig quedaría atrapada mostrando el build
  // viejo para siempre. Una vez que status es 'done', tratamos esa visita
  // como una nueva.
  const persisted = loaded && loaded.build?.status !== 'done' ? loaded : {};

  const [step, setStep] = useState(persisted.step || 0);
  const [agentType, setAgentType] = useState(persisted.agentType || 'builtin');
  const [agentOtherDescription, setAgentOtherDescription] = useState(persisted.agentOtherDescription || '');
  const [business, setBusiness] = useState({ ...DEFAULT_BUSINESS, ...(persisted.business || {}) });
  const [payments, setPayments] = useState({ ...DEFAULT_PAYMENTS, ...(persisted.payments || {}) });
  const [orders, setOrders] = useState({ ...DEFAULT_ORDERS, ...(persisted.orders || {}) });
  const [policies, setPolicies] = useState({ ...DEFAULT_POLICIES, ...(persisted.policies || {}) });
  const [interview, setInterview] = useState({ ...DEFAULT_INTERVIEW, ...(persisted.interview || {}) });
  const [build, setBuild] = useState({ ...DEFAULT_BUILD, ...(persisted.build || {}) });

  const goToStep = useCallback((n) => setStep(Math.max(0, Math.min(TOTAL_STEPS - 1, n))), []);
  const next = useCallback(() => setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1)), []);
  const back = useCallback(() => setStep((s) => Math.max(0, s - 1)), []);

  const setBusinessField = useCallback((field, value) => {
    setBusiness((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setPaymentEnabled = useCallback((method, enabled) => {
    setPayments((prev) => ({ ...prev, [method]: { ...prev[method], enabled } }));
  }, []);

  const setPaymentField = useCallback((method, field, value) => {
    setPayments((prev) => ({ ...prev, [method]: { ...prev[method], [field]: value } }));
  }, []);

  const setOrdersField = useCallback((field, value) => {
    setOrders((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setPoliciesField = useCallback((field, value) => {
    setPolicies((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setInterviewField = useCallback((field, value) => {
    setInterview((prev) => ({ ...prev, [field]: value }));
  }, []);

  const addInterviewMessage = useCallback((message) => {
    setInterview((prev) => ({ ...prev, messages: [...prev.messages, message] }));
  }, []);

  const setBuildStage = useCallback((stage, status) => {
    setBuild((prev) => ({ ...prev, stages: { ...prev.stages, [stage]: status } }));
  }, []);

  const setBuildStatus = useCallback((status) => {
    setBuild((prev) => ({ ...prev, status }));
  }, []);

  const reset = useCallback(() => {
    clearWizardIgState();
    setStep(0);
    setAgentType('builtin');
    setAgentOtherDescription('');
    setBusiness(DEFAULT_BUSINESS);
    setPayments(DEFAULT_PAYMENTS);
    setOrders(DEFAULT_ORDERS);
    setPolicies(DEFAULT_POLICIES);
    setInterview(DEFAULT_INTERVIEW);
    setBuild(DEFAULT_BUILD);
  }, []);

  // Persistir el estado para sobrevivir recargas y el ida-y-vuelta al login.
  useEffect(() => {
    saveWizardIgState({
      step, agentType, agentOtherDescription, business, payments, orders, policies, interview, build,
    });
  }, [step, agentType, agentOtherDescription, business, payments, orders, policies, interview, build]);

  return {
    step, goToStep, next, back, totalSteps: TOTAL_STEPS,
    agentType, setAgentType,
    agentOtherDescription, setAgentOtherDescription,
    business, setBusinessField,
    payments, setPaymentEnabled, setPaymentField,
    orders, setOrdersField,
    policies, setPoliciesField,
    interview, setInterviewField, addInterviewMessage,
    build, setBuildStage, setBuildStatus,
    reset,
  };
}
